import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { isTouchDevice } from '../../utils/helpers';
import {
  Container,
  InputsContainer,
  PredefinedSelectionButton,
  PredefinedSelectionsColumnContainer,
  PredefinedSelectionsRowContainer,
  RangeSelectionContainer,
  StyledDateRangePicker,
  StyledDateTimeInput,
} from './DateRangePicker.styles';
import { clampRangeToBounds, exceedsMaxDays, getRangeInvalidReason } from './dateRangeValidation';
import { RangeEndpoint, resolveRollingRange, RollingSelection } from './rollingRange';
import { useCalendarDecorations } from './useCalendarDecorations';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

interface PredefinedSelection {
  name: string;
  range: DateRange;
}

interface DateRangePickerProps {
  onChange?: (range: DateRange) => void;
  maxDaysCount?: number;
  singleDateSelection?: boolean;
  minDate?: Date;
  selectedRange?: DateRange;
  maximumDate?: Date;
  setIsWrongRange?: (isWrong: boolean) => void;
  highlightedDates?: Array<string | Date>;
  hidePredefinedSelections?: boolean;
  /**
   * Applies `maxDaysCount` as a rolling window around the date the user just picked instead of
   * rejecting an over long range: the other endpoint is cleared and asked for again.
   */
  rollingMaxDays?: boolean;
}

const DateRangePicker = ({
  onChange,
  maxDaysCount,
  singleDateSelection,
  minDate,
  selectedRange,
  maximumDate,
  setIsWrongRange,
  highlightedDates = [],
  hidePredefinedSelections = false,
  rollingMaxDays = false,
  ...rest
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const isRolling = rollingMaxDays && !!maxDaysCount && !singleDateSelection;
  const [valueRange, setValueRange] = useState(selectedRange || moment.range(moment(), moment()));
  const [valueFrom, setValueFrom] = useState<Date | Moment.Moment>(
    selectedRange?.start.clone().startOf('day') || minDate || undefined,
  );
  const [valueTo, setValueTo] = useState<Date | Moment.Moment>(selectedRange?.end?.clone().startOf('day') || undefined);
  const [predefinedSelectiom, setPredefinedSelectiom] = useState<PredefinedSelection>(undefined);
  const [startDateSelected, setStartDateSelected] = useState<Date>(undefined);
  const [pendingDate, setPendingDate] = useState<Date>(undefined);
  const [inputFromInvalid, setInputFromInvalid] = useState(false);
  const [inputToInvalid, setInputToInvalid] = useState(false);
  // Endpoint waiting for its counterpart after a rolling window restart.
  const [pendingAnchor, setPendingAnchor] = useState<Date>(undefined);
  const [rollingPreviewRange, setRollingPreviewRange] = useState<DateRange>(undefined);
  // Written during the library's own two click flow, where a re-render would cancel the selection.
  const anchorRef = useRef<Date>(undefined);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  const predefinedSelections: PredefinedSelection[] = [
    {
      name: t('components.dateRangePicker.last30days'),
      range: moment.range(moment().subtract(30, 'days'), moment()),
    },
    {
      name: t('components.dateRangePicker.thisMonth'),
      range: moment.range(moment().startOf('month'), moment().endOf('month')),
    },
    {
      name: t('components.dateRangePicker.lastMonth'),
      range: moment.range(
        moment().subtract(1, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month'),
      ),
    },
    {
      name: t('components.dateRangePicker.last180days'),
      range: moment.range(moment().subtract(179, 'days'), moment()),
    },
  ];

  const normalizeRange = (range: DateRange) => clampRangeToBounds({ range, minDate, maximumDate });

  const onPredefinedSelect = (selection: PredefinedSelection) => {
    const normalized = normalizeRange(selection.range);
    // Guarded by the disabled button, but a null range must never reach the picker's state.
    if (!normalized) return;

    handleSelect(normalized);
    setPredefinedSelectiom(selection);
    setStartDateSelected(undefined);
  };

  const isWrongRange = (range: DateRange) => exceedsMaxDays({ from: range.start, to: range.end, maxDaysCount });

  const clearInProgressSelection = useCallback(() => {
    setPendingAnchor(undefined);
    anchorRef.current = undefined;
    setPendingDate(undefined);
    setRollingPreviewRange(undefined);
  }, []);

  const PredefinedSelections = () => (
    <>
      {predefinedSelections.map((selection: PredefinedSelection, index: number) => {
        // A predefined range can sit entirely outside the bounds, e.g. "last month" once minDate is
        // an audience created this month.
        const isOutOfBounds = !normalizeRange(selection.range);

        return (
          <PredefinedSelectionButton
            key={selection.name}
            isFirst={index === 0}
            isSelected={selection.name === predefinedSelectiom?.name}
            isDisabled={isOutOfBounds}
            onClick={isOutOfBounds ? undefined : () => onPredefinedSelect(selection)}
          >
            {selection.name}
          </PredefinedSelectionButton>
        );
      })}
    </>
  );

  // Keeps only the date the user just picked, in the role they gave it, and clears the other one.
  const keepSingleEndpoint = (anchor: Date, endpoint: RangeEndpoint) => {
    const anchorMoment = moment(anchor);
    clearInProgressSelection();
    setPendingAnchor(anchor);
    setValueFrom(endpoint === 'start' ? anchor : undefined);
    setValueTo(endpoint === 'end' ? anchor : undefined);
    setValueRange(moment.range(anchorMoment, anchorMoment));
    setPredefinedSelectiom(undefined);
    setStartDateSelected(undefined);
  };

  const commitRange = (range: DateRange) => {
    clearInProgressSelection();
    setValueRange(range);
    setValueFrom(range.start);
    setValueTo(range.end);
    onChange?.(range);
  };

  const applyRollingSelection = (selection: RollingSelection) => {
    if ('range' in selection) {
      commitRange(selection.range);
      return;
    }

    keepSingleEndpoint(selection.date, selection.endpoint);
    toast.error(t('components.dateRangePicker.rollingMaxDaysError', { count: maxDaysCount }));
  };

  const selectRange = (range: DateRange) => {
    if (singleDateSelection) {
      // In single date mode, always allow selection
      setValueRange(range);
      setValueFrom(range.start);
      setValueTo(range.end);
      onChange?.(range);
      setIsWrongRange?.(false);
    } else if (isRolling) {
      const selectedDate =
        anchorRef.current && moment(anchorRef.current).isSame(range.start, 'day')
          ? range.end.toDate()
          : range.start.toDate();
      const retainedDate = moment(selectedDate).isSame(range.start, 'day') ? range.end.toDate() : range.start.toDate();

      applyRollingSelection(resolveRollingRange({ retainedDate, selectedDate, maxDaysCount }));
    } else if (isWrongRange(range)) {
      toast.error(t('components.dateRangePicker.maxDaysError', { count: maxDaysCount }));
    } else {
      setValueRange(range);
      setValueFrom(range.start);
      setValueTo(range.end);
      onChange?.(range);
    }
  };

  const handleSelectStart = (selectStartDate: Date | Moment.Moment) => {
    const startDate: Date = Moment.isMoment(selectStartDate) ? selectStartDate.toDate() : selectStartDate;

    if (isRolling && pendingAnchor) {
      // The library lost its own anchor on our restart re-render, so it only reports select starts:
      // complete the range from the endpoint we kept.
      applyRollingSelection(
        resolveRollingRange({ retainedDate: pendingAnchor, selectedDate: startDate, maxDaysCount }),
      );
      return;
    }

    if (!isTouchDevice() && !singleDateSelection) {
      // For non-touch devices in range mode, don't handle here. The anchor is kept in a ref because
      // a re-render at this point would cancel the library's in-flight selection.
      anchorRef.current = startDate;
      return;
    }
    const date: Date = startDate;
    setPendingDate(date);
    if (singleDateSelection) {
      // For single date selection, immediately select the date
      const singleDateRange = moment.range(date, date);
      selectRange(singleDateRange);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else if (startDateSelected) {
      const range =
        startDateSelected < date ? moment.range(startDateSelected, date) : moment.range(date, startDateSelected);
      selectRange(range);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else {
      setStartDateSelected(date);
      anchorRef.current = date;
      setValueFrom(date);
      setValueRange(moment.range(date, date));
    }
  };

  const handleSelect = (range: DateRange) => {
    if (singleDateSelection) {
      // For single date selection, create a range with the same start and end date
      const singleDateRange = moment.range(range.start, range.start);
      selectRange(singleDateRange);
      setStartDateSelected(undefined);
      setPredefinedSelectiom(undefined);
    } else {
      selectRange(range);
      setPredefinedSelectiom(undefined);
    }

    // The legacy calendar owns its desktop first click outside React state. Once it reports the
    // completed range, its temporary anchor must no longer override the persisted endpoints.
    anchorRef.current = undefined;
  };

  const onChangeInputValue = (range: DateRange | null) => {
    setPredefinedSelectiom(undefined);
    // A typed date can leave the range entirely outside the bounds. The inputs keep showing it and
    // their error surfaces why, but there is nothing to publish.
    if (!range) return;

    if (range.end >= range.start) {
      setValueRange(range);
    }
    onChange?.(range);
  };

  const onChangeStartValue = (date: Date) => {
    if (isRolling) {
      onChangeRollingValue(date, 'start');
      return;
    }
    const startMoment = moment(date).startOf('day');
    // Use existing end date if it exists and is after start, otherwise set to same day
    let endMoment = valueRange.end ? moment(valueRange.end).startOf('day') : startMoment.clone();

    // If end is before or same as start, set end to end of start day (single day range)
    if (endMoment.isSameOrBefore(startMoment, 'day')) {
      endMoment = startMoment.clone().endOf('day');
    } else {
      endMoment = endMoment.endOf('day');
    }

    const range = moment.range(startMoment, endMoment);
    setValueFrom(startMoment.toDate());
    onChangeInputValue(normalizeRange(range));
  };

  const onChangeRollingValue = (date: Date, endpoint: RangeEndpoint) => {
    // While a restart is pending only one input holds a value: editing the other one pairs with the
    // survivor, editing the survivor itself just moves it.
    const isEditingSurvivor = endpoint === 'start' ? !!valueFrom : !!valueTo;
    setPredefinedSelectiom(undefined);

    let retainedDate = endpoint === 'start' ? valueTo : valueFrom;
    if (pendingAnchor) retainedDate = isEditingSurvivor ? undefined : pendingAnchor;

    if (!retainedDate) {
      keepSingleEndpoint(date, endpoint);
      return;
    }

    const pickedPastRetained =
      endpoint === 'start' ? moment(date).isAfter(retainedDate, 'day') : moment(date).isBefore(retainedDate, 'day');
    if (pickedPastRetained) {
      commitRange(moment.range(moment(date), moment(date)));
      return;
    }

    applyRollingSelection(
      resolveRollingRange({
        retainedDate: moment(retainedDate).toDate(),
        selectedDate: date,
        selectedEndpoint: endpoint,
        maxDaysCount,
      }),
    );
  };

  const onChangeEndValue = (date: Date) => {
    if (isRolling) {
      onChangeRollingValue(date, 'end');
      return;
    }
    const endMoment = moment(date).startOf('day');
    const startMoment = valueRange.start ? moment(valueRange.start).startOf('day') : endMoment.clone();

    // If end is before start, set start to same day as end (single day range)
    if (endMoment.isBefore(startMoment, 'day')) {
      const range = moment.range(endMoment, endMoment.clone().endOf('day'));
      setValueFrom(endMoment.toDate());
      setValueTo(endMoment.clone().endOf('day').toDate());
      onChangeInputValue(normalizeRange(range));
    } else {
      const range = moment.range(startMoment, endMoment.clone().endOf('day'));
      setValueTo(endMoment.clone().endOf('day').toDate());
      onChangeInputValue(normalizeRange(range));
    }
  };

  React.useEffect(() => {
    // FIX: there was an issue on the mobile devices: touhed date cell is selecting after scroll/swipe action
    const handleDateTouchEnd = (e) => {
      e.stopPropagation();
    };

    window.addEventListener('touchmove', function () {
      const elements = document.getElementsByClassName('DateRangePicker__Date');
      for (let i = 0; i < elements.length; i++) {
        elements[i].addEventListener('touchend', handleDateTouchEnd);
      }
    });

    window.addEventListener('touchend', function () {
      const elements = document.getElementsByClassName('DateRangePicker__Date');
      for (let i = 0; i < elements.length; i++) {
        elements[i].removeEventListener('touchend', handleDateTouchEnd);
      }
    });
  }, []);

  // Single derived source of truth: the two inputs and the range bounds used to each push into
  // setIsWrongRange independently, so the last writer could leave the Apply button stuck.
  const isInvalidSelection = useMemo(() => {
    if (inputFromInvalid || inputToInvalid) return true;
    // A rolling restart leaves the selection half done on purpose until the other date is picked.
    if (isRolling && pendingAnchor) return true;

    return !!getRangeInvalidReason({
      from: valueFrom,
      to: valueTo,
      minDate,
      maximumDate,
      // Outside rolling mode an over long range is rejected on selection, never by disabling submit.
      maxDaysCount: isRolling ? maxDaysCount : undefined,
      requireComplete: isRolling,
    });
  }, [
    valueFrom,
    valueTo,
    maximumDate,
    minDate,
    maxDaysCount,
    inputFromInvalid,
    inputToInvalid,
    isRolling,
    pendingAnchor,
  ]);

  useEffect(() => {
    setIsWrongRange?.(isInvalidSelection);
  }, [isInvalidSelection]);

  // Sync selectedRange prop to internal state
  useEffect(() => {
    if (selectedRange) {
      clearInProgressSelection();
      setValueRange(selectedRange);
      setValueFrom(selectedRange.start.clone().startOf('day'));
      setValueTo(selectedRange.end?.clone().startOf('day') || selectedRange.start.clone().startOf('day'));
    } else if (selectedRange === undefined) {
      const defaultRange = moment.range(moment(), moment());
      setValueRange(defaultRange);
      setValueFrom(minDate || undefined);
      setValueTo(undefined);
    }
  }, [selectedRange, minDate, clearInProgressSelection]);

  const shouldSkipPendingState = () => {
    if (!isTouchDevice() || !pendingDate) return false;
    return !valueRange.contains(pendingDate);
  };

  const handleHighlightDate = (highlightedDate: Moment.Moment) => {
    if (!isRolling || !pendingAnchor) return;

    const selectedDate = highlightedDate.toDate();
    const anchorIsFirst = moment(pendingAnchor).isSameOrBefore(selectedDate, 'day');
    setRollingPreviewRange(
      moment.range(
        anchorIsFirst ? moment(pendingAnchor) : moment(selectedDate),
        anchorIsFirst ? moment(selectedDate) : moment(pendingAnchor),
      ),
    );
  };

  useCalendarDecorations({
    calendarContainerRef,
    highlightedDates,
    inProgressAnchorRef: anchorRef,
    isRolling,
    pendingAnchor,
    pendingDate,
    rollingPreviewRange,
    setRollingPreviewRange,
    singleDateSelection,
    startDateSelected,
    valueRange,
  });

  const getInputError = (isFrom: boolean) => {
    const inputId = isFrom ? 'inputFrom' : 'inputTo';
    if (document.getElementById(inputId) !== document.activeElement) return undefined;

    if (moment(valueFrom) > moment(valueTo)) {
      return isFrom ? t('components.input.invalidFromDate') : t('components.input.invalidToDate');
    }

    const value = isFrom ? valueFrom : valueTo;
    if (!value) return undefined;

    const reason = getRangeInvalidReason({ from: value, minDate, maximumDate });
    if (reason === 'beforeMin') return t('components.input.minimumDateError');
    if (reason === 'afterMax') return t('components.input.maximumDateError');

    return undefined;
  };

  return (
    <Container {...rest}>
      <RangeSelectionContainer ref={calendarContainerRef}>
        <InputsContainer>
          {singleDateSelection ? (
            <StyledDateTimeInput
              id="inputDate"
              label={t('components.dateRangePicker.selectDate')}
              value={valueFrom}
              onChange={onChangeStartValue}
              error={getInputError(true)}
              setIsWrongDate={setInputFromInvalid}
            />
          ) : (
            <>
              <StyledDateTimeInput
                id="inputFrom"
                label={t('components.dateRangePicker.from')}
                value={valueFrom}
                onChange={onChangeStartValue}
                error={getInputError(true)}
                setIsWrongDate={setInputFromInvalid}
              />
              <StyledDateTimeInput
                id="inputTo"
                label={t('components.dateRangePicker.to')}
                value={valueTo}
                onChange={onChangeEndValue}
                error={getInputError(false)}
                setIsWrongDate={setInputToInvalid}
              />
            </>
          )}
        </InputsContainer>
        {!singleDateSelection && !hidePredefinedSelections && (
          <PredefinedSelectionsRowContainer>
            <PredefinedSelections />
          </PredefinedSelectionsRowContainer>
        )}
        <StyledDateRangePicker
          numberOfCalendars={2}
          singleDateRange={singleDateSelection}
          value={valueRange}
          onSelect={handleSelect}
          onSelectStart={handleSelectStart}
          onHighlightDate={handleHighlightDate}
          skipPendingState={shouldSkipPendingState()}
          maximumDate={maximumDate}
          minimumDate={minDate}
        />
      </RangeSelectionContainer>
      {!singleDateSelection && !hidePredefinedSelections && (
        <PredefinedSelectionsColumnContainer>
          <PredefinedSelections />
        </PredefinedSelectionsColumnContainer>
      )}
    </Container>
  );
};

export default DateRangePicker;
